/**
 * 사흘 — **개인물품과 계측기.**
 *
 * `scenario/사흘/05_단서표.md` §3 을 라이브러리 70개와 한 장씩 대조하고 남은 구멍만
 * 여기 채운다. 노트북(`laptop_open`)·복합기(`mfp_printer`)·데스크탑(`desk_pc`)·
 * 연탄난로(`briquette_stove`)처럼 **이미 있는 것은 절대 다시 만들지 않는다** —
 * 색만 다른 중복은 draw call 만 늘리고 조사 화면에서는 구별되지 않는다.
 *
 * 채우는 구멍:
 *
 * | 카드 | 물건 | 없어서 막혀 있던 것 |
 * |---|---|---|
 * | E16·E24·E26 | `seoryu_bongtu` | 담보봉투·개인봉투·검사서봉투 셋이 같은 물건을 쓴다 |
 * | E31 | `jageop_gabang` + `folded_memo` | 「안쪽 포켓의 접힌 메모」 구조 자체 |
 * | E35 | `camera_bag` + `pocket_note` | 송이경 감정선의 유일한 물증 |
 * | E23 | `power_logger` | 명주원 22시대 공백 |
 * | E04 | `case_board` | R3 전환 자동공개 |
 *
 * **상표 금지가 이 파일에서도 첫 번째 규칙이다.** 로고·글자마크는 물론이고 특정
 * 제품을 지목하게 만드는 형태(브랜드 특유의 버클 실루엣, 각인된 금속판, 특정 카메라
 * 가방의 삼각 플랩)를 쓰지 않는다. 어느 제조사에나 있는 범용 형태만 쓴다.
 *
 * 판정 기준은 `saholEvidence.ts` 와 같다 — 「그 물건으로 보이는가」가 아니라
 * **「무엇이 잘못됐는지가 형태로 보이는가」**다. 봉투는 열려서 서류가 밀려 나와 있고,
 * 가방은 주저앉아 안쪽 포켓이 벌어져 있고, 기록계 그래프는 22시 자리가 비어 있다.
 * 멀쩡한 물건에 설명문을 붙이면 조사가 텍스트 읽기로 퇴화한다.
 *
 * 좌표 규약은 `CONVENTIONS.md` 그대로 — 원점은 바닥 한가운데, 정면은 -Z, 미터.
 * 벽에 붙는 `power_logger` 만 `hangs` + `sunk` 를 **둘 다** 단다(하나만 달면 게이트에
 * 걸린다).
 */

import * as THREE from 'three';
import { bevelBox } from './kernel';
import { paintGeometry, type Piece } from './pieces';
import { printedFace, litFace, rnd, ribbon, spline, stroke, atlasUV, seg7 } from './saholEvidence';

/* ------------------------------------------------------------------ *
 * 도구
 * ------------------------------------------------------------------ */

/** -Z 를 보는 면. `PlaneGeometry` 는 +Z 를 보므로 반드시 뒤집는다 */
function facingFront(w: number, h: number) {
  const p = new THREE.PlaneGeometry(w, h);
  p.rotateY(Math.PI);
  return p;
}

/** 위를 보는 면. rotateX 만 하면 글의 위쪽이 -Z 를 향해 **180도 뒤집혀 읽힌다** */
function facingUp(w: number, d: number) {
  const p = new THREE.PlaneGeometry(w, d);
  p.rotateX(-Math.PI / 2);
  p.rotateY(Math.PI);
  return p;
}

/** 종이 한 장이 널브러진 각도. 자로 잰 듯 나란하면 「배치한 티」가 난다 */
function scatter(g: THREE.BufferGeometry, seed: number, amt = 1) {
  g.rotateY((rnd(seed) - 0.5) * 0.22 * amt);
  g.rotateZ((rnd(seed + 11) - 0.5) * 0.05 * amt);
  g.rotateX((rnd(seed + 23) - 0.5) * 0.05 * amt);
  return g;
}

/* ------------------------------------------------------------------ *
 * 물건
 * ------------------------------------------------------------------ */

export const SAHOL_PERSONAL_PIECES: Piece[] = [
  {
    id: 'seoryu_bongtu',
    name: '서류봉투',
    collection: 'sahol',
    category: '단서',
    note:
      'E16 담보봉투 · E24 개인 서류봉투 · E26 검사서 봉투가 공유한다. **닫아 두면 안 된다** — ' +
      '봉투는 「안에 뭐가 들었나」가 전부라, 봉해 놓으면 조사가 성립하지 않고 그냥 갈색 판이 된다. ' +
      '입구 덮개가 젖혀져 있고 서류 세 장이 부채꼴로 밀려 나와야 「방금 누가 꺼내 봤다」가 읽힌다. ' +
      '여밈끈은 풀려서 봉투 위에 늘어져 있어야 한다 — 끈이 단추에 감겨 있으면 열린 게 아니다. ' +
      '크라프트지라 흰 종이보다 훨씬 어둡고 누렇다. 하얗게 만들면 서류철로 오인된다.',
    dims: { w: 0.235, h: 0.034, d: 0.325 },
    clusters: 1,
    /* 덮개는 입구(-Z) 경첩에서 젖혀진 채 구워 있다 — E 를 누르면 덮인다.
     * 밀려 나온 서류 세 장은 더 당겨 꺼낼 수 있다 */
    openable: [
      { part: '덮개', swing: -0.95, axis: 'x', pivot: [0, 0.031, -0.156], inside: { w: 0.2, h: 0.025, d: 0.29, at: [0, 0.017, 0] } },
      { part: '서류', slide: [0, 0.04, -0.06] },
    ],
    build(w, d) {
      const TOP = d.h;
      // 덮개 경첩이 -Z 입구에 오도록 미리 푼 값. rotateX(+0.95) 이므로
      // 자유단은 위로 그리고 -Z(보는 쪽)로 넘어간다
      const FLAP_Y = 0.0386;
      const FLAP_Z = 0.0276;

      w.part('봉투 몸통');
      // 베벨을 크게 준다 — 서류가 들어 모서리가 부푼 종이봉투는 각이 서지 않는다
      const body = bevelBox(d.w, d.h, d.d, 0.0065, 3);
      body.translate(0, d.h / 2, 0);
      paintGeometry(body, '#a98a5c');
      w.add(body, 'paper', 0.55);

      w.part('덮개');
      w.movable('덮개', () => {
        const flap = bevelBox(d.w, 0.0018, 0.096, 0.0013);
        flap.rotateX(0.95);
        // 경첩을 몸통 안으로 3mm 밀어 넣는다. 딱 맞추면 joint-check 가 조각을 쪼갠다
        flap.translate(0, TOP + FLAP_Y - 0.003, -d.d / 2 - FLAP_Z + 0.006);
        paintGeometry(flap, '#9e8054');
        w.add(flap, 'paper', 0.5);
      });

      w.part('내용물');
      // 세 장이 조금씩 다른 각도로 밀려 나온다. 한 덩어리로 만들면 나무토막이다
      w.movable('서류', () => {
        for (let i = 0; i < 3; i++) {
        const sh = bevelBox(d.w * (0.9 - i * 0.035), 0.0014, d.d * 0.92, 0.0008);
        scatter(sh, i * 9 + 3, 0.5);
        sh.rotateX(0.028 + i * 0.014);
        sh.translate((rnd(i * 5) - 0.5) * 0.007, d.h * 0.3 + i * 0.006, -0.024 - i * 0.011);
        w.add(sh, 'paper', 0.7);
        }
      });

      w.part('여밈 아일릿');
      // 몸통 쪽 하나, 덮개 쪽 하나. 둘 다 있어야 「끈으로 봉하는 봉투」다
      const eyeA = new THREE.CylinderGeometry(0.0062, 0.0062, 0.0032, 12);
      eyeA.translate(0, TOP, -d.d / 2 + 0.048);
      w.add(eyeA, 'brass', 0.05);
      const eyeB = new THREE.CylinderGeometry(0.0062, 0.0062, 0.0032, 12);
      eyeB.rotateX(0.95);
      eyeB.translate(0, TOP + FLAP_Y + 0.0244 - 0.003, -d.d / 2 - FLAP_Z - 0.0175 + 0.006);
      w.add(eyeB, 'brass', 0.05);

      w.part('여밈끈');
      // 풀려서 몸통 위에 한 바퀴 늘어졌다. 감겨 있으면 봉해진 것으로 읽힌다
      const cord = spline(
        [
          [0, TOP + 0.060, -d.d / 2 - 0.040],
          [0.020, TOP + 0.028, -d.d / 2 - 0.024],
          [0.031, TOP + 0.0022, -d.d / 2 + 0.020],
          [0.004, TOP + 0.0022, -d.d / 2 + 0.062],
          [-0.030, TOP + 0.0022, -d.d / 2 + 0.044],
          [-0.038, TOP + 0.0022, -d.d / 2 + 0.012],
        ],
        24,
      );
      ribbon(w, cord, 0.0034, 0.0012, 'fabric');

      w.part('라벨');
      w.thinSurface(() => {
        const p = facingUp(d.w * 0.52, d.d * 0.19);
        p.translate(0.004, TOP + 0.0006, 0.072);
        w.add(p, 'painted', 0);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 손으로 쓴 이름표. 글자를 진짜로 그리면 서버 폰트가 없어 두부가 된다
          g.strokeStyle = 'rgba(24,30,44,0.82)';
          for (let i = 0; i < 2; i++) {
            stroke(g, W * 0.08, H * (0.4 + i * 0.28), W * (0.42 + rnd(i * 3) * 0.42), H * (0.4 + i * 0.28), H * 0.075, i * 7);
          }
          // 취급 도장 한 번 — 봉투를 「돌아다닌 서류」로 만든다
          g.strokeStyle = 'rgba(150,40,36,0.5)';
          g.lineWidth = H * 0.05;
          g.strokeRect(W * 0.7, H * 0.24, W * 0.22, H * 0.42);
        },
        320,
        128,
        '#c8ad7e',
      ),
    }),
  },

  {
    id: 'folded_memo',
    name: '접힌 메모',
    collection: 'sahol',
    category: '단서',
    note:
      'E31. 윤가람이 작업가방 안쪽 포켓에 접어 넣어 둔 개인메모. **평평한 판으로 만들면 안 된다** — ' +
      '네 번 접혔다 펴진 종이는 접힌 자국을 따라 네 면이 서로 다른 각도로 들려 있고, 한 귀퉁이는 ' +
      '아예 뜬다. 그 「다시 접히려는 힘」이 보여야 「몰래 넣어 둔 것」으로 읽힌다. ' +
      '펼쳐진 면의 글은 급하게 쓴 필기라 줄이 흔들리고 아래로 갈수록 여백이 없어진다.',
    dims: { w: 0.104, h: 0.011, d: 0.146 },
    clusters: 1,
    /* 메모를 통째로 뒤집는다 — 먼 쪽(-Z) 가장자리를 축으로 들어 넘긴다 */
    openable: [{ part: '메모', swing: 3.14, axis: 'x', pivot: [0, 0.001, -0.073] }],
    build(w, d) {
      w.part('종이');
      w.movable('메모', () => {
      // 접힌 자국 네 칸. 칸마다 각도가 달라야 「폈다」가 된다
      const quads: [number, number, number, number, number][] = [
        // [x부호, z부호, 기울기X, 기울기Z, 들림]
        [-1, -1, 0.055, 0.03, 0.0012],
        [1, -1, -0.02, -0.075, 0.0038],
        [-1, 1, -0.06, 0.018, 0.0009],
        [1, 1, 0.028, -0.045, 0.0021],
      ];
      quads.forEach(([sx, sz, rx, rz, lift], i) => {
        // 칸을 1mm 씩 겹쳐 만든다. 딱 맞추면 접힌 선에서 조각이 넷으로 쪼개진다
        const q = bevelBox(d.w / 2 + 0.0016, 0.0011, d.d / 2 + 0.0016, 0.0004);
        q.rotateX(rx);
        q.rotateZ(rz);
        q.translate((sx * d.w) / 4, 0.0009 + lift, (sz * d.d) / 4);
        atlasUV(q, i % 2, Math.floor(i / 2), 2, 2);
        w.add(q, 'painted', 0);
      });

      w.part('접힌 자국');
      // 접힌 선은 종이가 두 번 겹친 자리라 실제로 도톰하다. 이 능선이 없으면
      // 「네 조각을 늘어놓은 것」으로 보인다
      const ridgeX = bevelBox(0.0026, 0.0016, d.d * 0.94, 0.0005);
      ridgeX.translate(0, 0.0018, 0);
      paintGeometry(ridgeX, '#d9d3c4');
      w.add(ridgeX, 'paper', 0.5);
      const ridgeZ = bevelBox(d.w * 0.94, 0.0016, 0.0026, 0.0005);
      ridgeZ.translate(0, 0.0018, 0);
      paintGeometry(ridgeZ, '#d9d3c4');
      w.add(ridgeZ, 'paper', 0.5);
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          // 2x2 아틀라스. 칸마다 다른 필기가 있어야 네 면이 한 장으로 이어져 읽힌다
          const cw = W / 2;
          const ch = H / 2;
          for (let c = 0; c < 2; c++) {
            for (let r = 0; r < 2; r++) {
              const ox = c * cw;
              const oy = r * ch;
              // 접힌 자국을 따라 생긴 그늘 — 종이가 한 장으로 이어져 보이게 한다
              const grad = g.createLinearGradient(ox, oy, ox + cw, oy + ch);
              grad.addColorStop(0, 'rgba(150,140,120,0.16)');
              grad.addColorStop(0.5, 'rgba(255,255,255,0)');
              grad.addColorStop(1, 'rgba(150,140,120,0.13)');
              g.fillStyle = grad;
              g.fillRect(ox, oy, cw, ch);
              g.strokeStyle = 'rgba(26,34,52,0.78)';
              const lines = 5 + Math.floor(rnd(c * 7 + r * 13) * 3);
              for (let i = 0; i < lines; i++) {
                const y = oy + ch * (0.16 + (i / lines) * 0.74);
                // 아래로 갈수록 줄이 길어진다 — 여백이 없어지는 급한 필기
                const len = cw * (0.34 + (i / lines) * 0.5 + rnd(i + c * 5 + r * 3) * 0.16);
                stroke(g, ox + cw * 0.1, y, ox + cw * 0.1 + len, y, ch * 0.028, i + c * 17 + r * 29);
              }
            }
          }
        },
        512,
        512,
        '#e9e3d4',
      ),
    }),
  },

  {
    id: 'jageop_gabang',
    name: '작업가방',
    collection: 'sahol',
    category: '단서',
    note:
      'E31. 윤가람의 캔버스 작업가방. **각 잡힌 상자로 만들면 안 된다** — 캔버스 토트는 세워 두면 ' +
      '입구가 주저앉고 옆면이 안으로 접힌다. 그 무너짐이 없으면 종이가방이다. ' +
      '단서는 「안쪽 포켓」에 있으므로 **입구가 벌어져 안쪽 포켓이 보여야 한다.** ' +
      '도예가의 가방이라 아랫단에 흙물이 배어 올라와 있다 — 위아래 색이 갈리는 그 경계가 ' +
      '이 가방을 직업의 물건으로 만든다. 손잡이 하나는 서 있고 하나는 옆으로 넘어가 있다.',
    dims: { w: 0.3, h: 0.29, d: 0.148 },
    /* **포켓을 벌려야 메모가 나온다** (E31). 포켓은 +Z 안쪽 벽에 덧댄 판이라 아랫단을 축으로
     * 앞(-Z)으로 젖힌다 — 담는 곳은 포켓과 벽 사이다. 축 좌표는 정적이라 dims 를 못 읽는다:
     * 포켓 아랫단 y = d.h*0.42 − d.h*0.2 = 0.064 · z = d.d/2 − T − 0.0016 ≒ 0.066
     * 열림은 0.7rad(40°) 이면 충분하다 — 천 포켓은 문짝처럼 90° 로 안 벌어진다 */
    openable: [
      { part: '안쪽 포켓', swing: -0.7, axis: 'x', pivot: [0, 0.064, 0.066],
        inside: { w: 0.16, h: 0.07, d: 0.04, at: [-0.012, 0.11, 0.05] } },
    ],
    clusters: 1,
    build(w, d) {
      const T = 0.006; // 캔버스 두께
      const STAIN = d.h * 0.34; // 흙물이 배어 올라온 높이
      const CANVAS = '#c8bda4';
      const MUD = '#8a7a61';

      // 벽마다 기울기가 다르다. **축은 반드시 바닥(y=0)** 이어야 한다 — 상자 제 중심에서
      // 돌리면 위아래 두 토막이 서로 어긋나 흙물 경계에 1cm 단차가 생긴다
      // 부호를 틀리면 벽이 **바깥으로** 벌어지고 손잡이가 테에서 3cm 뜬다.
      // rotateX(t) 는 z' = y·sin t 이므로 -Z 벽의 위가 안쪽(+Z)으로 오려면 t > 0 이다
      const TZ = 0.075; // -Z 벽: 위가 +Z(안)로 접힌다
      const TX = 0.055; // +X 벽: 위가 -X(안)로 접힌다
      /** 입구 테의 실제 z — 손잡이는 여기에 물려야 한다. 손으로 찍으면 반드시 뜬다 */
      const RIM_Z = d.d / 2 - T / 2 - (d.h - 0.005) * Math.sin(TZ);
      const RIM_Y = d.h - 0.006;
      /** 바닥을 축으로 세운 벽 조각 */
      const tilted = (
        g: THREE.BufferGeometry,
        y: number,
        px: number,
        pz: number,
        tilt: number,
        axis: 'x' | 'z',
      ) => {
        g.translate(0, y, 0);
        if (axis === 'x') g.rotateX(tilt);
        else g.rotateZ(tilt);
        g.translate(px, 0, pz);
      };

      w.part('몸통');
      // 벽 넷을 위아래로 쪼갠다. 흙물 자국을 정점색으로 굽기 위해서다 —
      // 슬롯을 하나 더 쓰면 draw call 이 늘고, 텍스처를 쓰면 UV 를 짜야 한다
      const wall = (
        ww: number,
        dd: number,
        px: number,
        pz: number,
        tilt: number,
        axis: 'x' | 'z',
      ) => {
        for (const [y0, y1, hex] of [
          [0, STAIN, MUD],
          [STAIN, d.h, CANVAS],
        ] as const) {
          const g = bevelBox(ww, y1 - y0 + 0.0012, dd, 0.0018);
          tilted(g, (y0 + y1) / 2, px, pz, tilt, axis);
          paintGeometry(g, hex);
          w.add(g, 'fabric', 0.5);
        }
      };
      wall(d.w, T, 0, -d.d / 2 + T / 2, TZ, 'x');
      wall(d.w, T, 0, d.d / 2 - T / 2, -TZ, 'x');
      wall(T, d.d, -d.w / 2 + T / 2, 0, -TX, 'z');
      wall(T, d.d, d.w / 2 - T / 2, 0, TX, 'z');

      w.part('바닥');
      const base = bevelBox(d.w - T, T * 1.5, d.d - T, 0.002);
      base.translate(0, T * 0.75, 0);
      paintGeometry(base, MUD);
      w.add(base, 'fabric', 0.5);

      w.part('바닥 보강');
      // 가죽 바닥판. 캔버스 가방이 바닥에서 먼저 상하는 자리라 실제로 다들 이걸 댄다
      const sole = bevelBox(d.w * 0.94, 0.005, d.d * 0.9, 0.0022);
      sole.translate(0, 0.0025, 0);
      w.add(sole, 'leather', 0.4);

      /* 포켓은 통째로 움직인다 — 판과 그 위 테가 같이 벌어져야 「손을 넣었다」가 된다.
       * 테만 따로 두면 판은 붙은 채 테만 날아간다 */
      w.movable('안쪽 포켓', () => {
        // +Z 안쪽 벽에 덧댄 포켓. 벽과 **같은 기울기**로 붙여야 실제로 닿는다 —
        // 벽이 기운 걸 잊고 수직으로 세우면 위쪽이 1cm 떠서 게이트에 걸린다
        const pocket = bevelBox(d.w * 0.58, d.h * 0.4, 0.0042, 0.0016);
        tilted(pocket, d.h * 0.42, -0.012, d.d / 2 - T - 0.0016, -TZ, 'x');
        paintGeometry(pocket, '#b5a98f');
        w.add(pocket, 'fabric', 0.5);
        // 입구는 포켓 위에 겹쳐 얹은 테가 안쪽(-Z)으로 밀려나 벌어져 있다.
        // 이 벌어짐이 「여기서 메모가 나왔다」를 만든다 — 없으면 벽에 붙은 판때기다
        const hem = bevelBox(d.w * 0.58, 0.019, 0.0044, 0.0014);
        tilted(hem, d.h * 0.615, -0.012, d.d / 2 - T - 0.0085, -TZ, 'x');
        paintGeometry(hem, '#a2957b');
        w.add(hem, 'fabric', 0.5);
      });

      w.part('손잡이');
      // 하나는 서 있고 하나는 옆으로 넘어갔다. 둘 다 세우면 진열대 위 상품이 된다
      const up = spline(
        [
          [-0.08, RIM_Y, -RIM_Z],
          [-0.089, d.h + 0.055, -0.032],
          [-0.086, d.h + 0.068, 0.014],
          [-0.078, RIM_Y, RIM_Z],
        ],
        18,
      );
      ribbon(w, up, 0.021, 0.0034, 'fabric');
      const flop = spline(
        [
          [0.079, RIM_Y, -RIM_Z],
          [0.132, d.h - 0.012, -0.036],
          [0.151, d.h - 0.052, 0.008],
          [0.077, RIM_Y, RIM_Z],
        ],
        18,
      );
      ribbon(w, flop, 0.021, 0.0034, 'fabric');

      w.part('입구 테');
      // 벌어진 입을 잡아 주는 테. 이게 없으면 위쪽 네 벽이 각자 떠 보인다
      for (const [ww, dd, px, pz, tilt, axis] of [
        [d.w, 0.009, 0, -d.d / 2 + T / 2, TZ, 'x'],
        [d.w, 0.009, 0, d.d / 2 - T / 2, -TZ, 'x'],
        [0.009, d.d, -d.w / 2 + T / 2, 0, -TX, 'z'],
        [0.009, d.d, d.w / 2 - T / 2, 0, TX, 'z'],
      ] as const) {
        const g = bevelBox(ww, 0.012, dd, 0.0022);
        tilted(g, d.h - 0.005, px, pz, tilt, axis as 'x' | 'z');
        paintGeometry(g, '#b9ad93');
        w.add(g, 'fabric', 0.5);
      }
    },
  },

  {
    id: 'camera_bag',
    nofront: true,
    name: '카메라가방',
    collection: 'sahol',
    category: '단서',
    note:
      'E35. 송이경 주택 현관 옆에 놓인 숄더백. 안에서 작은 수첩이 나온다. ' +
      '**서 있는 가방으로 만들면 안 된다** — 현관에 던져 둔 가방은 어깨끈이 바닥에 풀려 ' +
      '똬리를 튼다. 그 끈이 「방금 벗어 놓았다」의 전부다. ' +
      '덮개는 버클 한쪽만 채워져 반쯤 들려 있고, 옆 지퍼포켓은 벌어져 있어야 한다 — ' +
      '수첩이 나온 자리가 보여야 조사가 이어진다. 상표 금지라 로고·각인판은 없고 ' +
      '무지 직조 라벨 한 장만 단다.',
    dims: { w: 0.33, h: 0.212, d: 0.155 },
    /* 덮개를 젖히면 속이 보인다. 뒤(+Z) 모서리에서 넘어가고, 담는 곳은 가방 속 —
     * 수첩(E35)을 에디터에서 여기 직접 넣을 수 있다.
     * **몸통이 통짜면 덮개를 젖혀도 속이 안 보인다**(2026-09-12 택배상자에서 겪은 것) →
     * 아래 `build` 에서 벽 넷 + 바닥으로 짓는다. 축 좌표는 정적이라 dims 를 못 읽는다:
     * 덮개 y = d.h*0.86 + 0.011 = 0.193 · 경첩 z = d.d/2 = 0.0775 */
    openable: [
      { part: '덮개', swing: 2.3, axis: 'x', pivot: [0, 0.193, 0.0775],
        inside: { w: 0.27, h: 0.13, d: 0.1, at: [0, 0.08, 0] } },
    ],
    clusters: 1,
    build(w, d) {
      w.part('몸통');
      /* 완충재가 든 벽이라 두껍다. 통짜 상자로 두면 덮개를 열어도 뚜껑 밑이 막혀 있다 */
      const BT = 0.013, bh = d.h * 0.86, by = 0.008;
      const wall = (ww: number, dd: number, px: number, pz: number) => {
        const g = bevelBox(ww, bh, dd, 0.006, 2);
        g.translate(px, by + bh / 2, pz);
        paintGeometry(g, '#3b4048');
        w.add(g, 'fabric', 0.6);
      };
      wall(d.w, BT, 0, -d.d / 2 + BT / 2);
      wall(d.w, BT, 0, d.d / 2 - BT / 2);
      wall(BT, d.d - BT * 2, -d.w / 2 + BT / 2, 0);
      wall(BT, d.d - BT * 2, d.w / 2 - BT / 2, 0);
      const base = bevelBox(d.w, BT, d.d, 0.006, 2);
      base.translate(0, by + BT / 2, 0);
      paintGeometry(base, '#34383f');
      w.add(base, 'fabric', 0.6);

      w.part('칸막이');
      /* 속을 비우고 나니 덮개를 젖혔을 때 **까만 빈 상자**가 됐다. 카메라가방은 실제로
       * 벨크로 칸막이로 칸이 나뉘어 있다 — 그게 있어야 「장비를 넣는 가방」으로 읽히고,
       * 수첩(E35)을 넣었을 때도 어디에 든 것인지 보인다 */
      for (const dx of [-0.055, 0.055] as const) {
        const div = bevelBox(0.011, bh - 0.03, d.d - BT * 2 - 0.006, 0.004, 2);
        div.translate(dx, by + BT + (bh - 0.03) / 2, 0);
        paintGeometry(div, '#565d68');
        w.add(div, 'fabric', 0.5);
      }

      w.movable('덮개', () => {
      // 위를 덮고 -Z 면으로 넘어간다. 자유단이 들려 있어야 「열어 봤다」가 된다
      const lid = bevelBox(d.w * 1.02, 0.0075, d.d * 0.98, 0.003);
      lid.translate(0, d.h * 0.86 + 0.011, 0.004);
      paintGeometry(lid, '#33383f');
      w.add(lid, 'fabric', 0.6);
      // 자락은 아래가 **-Z 로** 들려야 「덜 닫힌 것」이다. +Z 로 기울이면 몸통 쪽으로
      // 말려 들어가 오히려 더 닫혀 보인다 — 부호 하나가 뜻을 뒤집는다
      const SK_A = 0.42;
      const SK_Y = d.h * 0.86 - 0.022;
      const SK_Z = -d.d / 2 - 0.008;
      /** 자락에 붙는 부품을 자락 로컬좌표로 놓는다. 손으로 계산하면 반드시 뜬다 */
      const onSkirt = (g: THREE.BufferGeometry, lx: number, ly: number, lz: number) => {
        g.rotateX(SK_A);
        const c = Math.cos(SK_A);
        const sn = Math.sin(SK_A);
        g.translate(lx, SK_Y + ly * c - lz * sn, SK_Z + ly * sn + lz * c);
      };
      const skirt = bevelBox(d.w * 1.02, 0.075, 0.0075, 0.003);
      onSkirt(skirt, 0, 0, 0);
      paintGeometry(skirt, '#33383f');
      w.add(skirt, 'fabric', 0.6);

      w.part('버클');
      // 두 개 중 왼쪽만 채워져 있다. 둘 다 채우면 열린 게 아니고 둘 다 풀면 덮개가 뜬다
      for (const [bx, done] of [
        [-0.086, true],
        [0.086, false],
      ] as const) {
        const bk = bevelBox(0.032, 0.019, 0.0085, 0.0022);
        onSkirt(bk, bx, -0.026, -0.007);
        paintGeometry(bk, '#22262b');
        w.add(bk, 'plastic', 0.3);
        if (done) {
          // 채운 쪽은 웨빙이 자락을 지나 몸통 앞면까지 당겨져 있다
          const c = Math.cos(SK_A);
          const sn = Math.sin(SK_A);
          const by = SK_Y - 0.026 * c + 0.007 * sn;
          const bz = SK_Z - 0.026 * sn - 0.007 * c;
          ribbon(
            w,
            spline(
              [
                [bx, by, bz],
                [bx, by - 0.022, bz + 0.012],
                [bx, by - 0.042, -d.d / 2 + 0.003],
              ],
              8,
            ),
            0.026,
            0.0042,
            'fabric',
          );
        } else {
          // 푼 쪽은 웨빙 끝이 자락 아래로 삐져나와 늘어진다
          const wb = bevelBox(0.026, 0.034, 0.005, 0.0012);
          onSkirt(wb, bx, -0.049, -0.007);
          paintGeometry(wb, '#2a2e34');
          w.add(wb, 'fabric', 0.4);
        }
      }

      });

      w.part('옆 포켓');
      // +X 옆면 지퍼포켓이 벌어져 있다. 수첩이 나온 자리
      const pk = bevelBox(0.0075, d.h * 0.5, d.d * 0.74, 0.0026);
      pk.rotateZ(-0.1);
      pk.translate(d.w / 2 + 0.007, d.h * 0.42, 0);
      paintGeometry(pk, '#3f444c');
      w.add(pk, 'fabric', 0.6);
      const zip = bevelBox(0.0055, 0.0055, d.d * 0.74, 0.0014);
      zip.rotateZ(-0.1);
      zip.translate(d.w / 2 + 0.0105, d.h * 0.42 + d.h * 0.25, 0);
      w.add(zip, 'alu', 0.1);

      w.part('D링');
      for (const sx of [-1, 1]) {
        const ring = new THREE.TorusGeometry(0.011, 0.0026, 6, 14);
        ring.rotateY(Math.PI / 2);
        ring.translate(sx * (d.w / 2 - 0.004), d.h * 0.78, 0);
        w.add(ring, 'alu', 0.08);
      }

      w.part('어깨끈');
      // 바닥에 풀려 똬리를 튼다. 이게 이 조각의 전부다 — 걸어 두면 상점 진열이다
      const strap = spline(
        [
          [-(d.w / 2 - 0.004), d.h * 0.78, 0],
          [-(d.w / 2 + 0.075), d.h * 0.42, -0.06],
          [-(d.w / 2 + 0.115), 0.018, -0.13],
          [-(d.w / 2 + 0.03), 0.017, -0.2],
          [d.w / 2 - 0.06, 0.017, -0.175],
          [d.w / 2 + 0.06, 0.018, -0.085],
          [d.w / 2 + 0.05, d.h * 0.4, -0.02],
          [d.w / 2 - 0.004, d.h * 0.78, 0],
        ],
        34,
      );
      ribbon(w, strap, 0.028, 0.0042, 'fabric');

      w.part('라벨');
      // 무지 직조 라벨. 상표는 없고 박음질 자국만 — 이름 없는 가방은 오히려 어색하다
      w.thinSurface(() => {
        const p = facingFront(0.034, 0.014);
        p.translate(-0.11, d.h * 0.3, -d.d / 2 - 0.0012);
        w.add(p, 'painted', 0);
      });

      w.part('발');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const ft = new THREE.CylinderGeometry(0.008, 0.0088, 0.009, 10);
          ft.translate(sx * (d.w / 2 - 0.032), 0.0045, sz * (d.d / 2 - 0.03));
          w.add(ft, 'rubber', 0.1);
        }
      }
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(210,214,220,0.5)';
          g.lineWidth = H * 0.06;
          g.strokeRect(W * 0.08, H * 0.16, W * 0.84, H * 0.68);
        },
        128,
        64,
        '#454b53',
      ),
    }),
  },

  {
    id: 'pocket_note',
    name: '수첩',
    collection: 'sahol',
    category: '단서',
    note:
      'E35. 카메라가방에서 나온 손바닥 수첩. **덮여서 반듯한 수첩은 아무것도 말하지 않는다** — ' +
      '누가 몇 번이나 되돌아본 페이지가 있어야 한다. 그래서 볼펜이 갈피에 꽂혀 표지가 ' +
      '한쪽만 들려 있고, 고무밴드는 이미 벗겨져 옆으로 밀려나 있다. ' +
      '속지는 손때가 타 가장자리가 부풀고 모서리가 둥글게 닳아 있다 — 새 수첩처럼 각이 서면 ' +
      '「쓰던 물건」이 아니다.',
    dims: { w: 0.093, h: 0.021, d: 0.138 },
    clusters: 1,
    /* 앞표지가 왼쪽(-x) 등 경첩으로 열린다 — 책처럼 넘긴다. 갈피의 볼펜과
     * 벗겨진 고무밴드도 집어 들 수 있다 */
    openable: [
      { part: '앞표지', swing: 3.0, axis: 'z', pivot: [-0.0465, 0.018, 0], inside: { w: 0.085, h: 0.015, d: 0.13, at: [0, 0.008, 0] } },
      { part: '볼펜', slide: [0, 0.04, 0] },
      { part: '고무밴드', slide: [0, 0.04, 0.02] },
    ],
    build(w, d) {
      const PEN_Y = 0.0075; // 갈피의 볼펜이 표지를 들어 올린 높이

      w.part('뒤표지');
      const back = bevelBox(d.w, 0.0026, d.d, 0.0022, 2);
      back.translate(0, 0.0013, 0);
      w.add(back, 'leather', 0.35);

      w.part('속지');
      // 세 겹으로 나눠 부풀린다. 한 덩어리면 나무토막이고, 가장자리가 살아야 종이다
      for (let i = 0; i < 3; i++) {
        const blk = bevelBox(d.w * (0.965 - i * 0.006), 0.0038, d.d * (0.97 - i * 0.005), 0.0011, 2);
        blk.rotateZ((rnd(i * 7) - 0.5) * 0.02);
        blk.rotateX(0.004 * i);
        blk.translate((rnd(i * 3) - 0.5) * 0.0012, 0.0045 + i * 0.0037, (rnd(i * 5) - 0.5) * 0.0015);
        paintGeometry(blk, i === 2 ? '#ded7c6' : '#d6cfbd');
        w.add(blk, 'paper', 0.6);
      }

      w.part('앞표지');
      // 볼펜이 갈피에 있어 -Z 쪽이 들린다. 평평하면 「덮인 새 수첩」이다
      w.movable('앞표지', () => {
        const front = bevelBox(d.w, 0.0026, d.d, 0.0022, 2);
        front.rotateX(-0.115);
        front.translate(0, 0.0175 + PEN_Y * 0.28, 0.002);
        w.add(front, 'leather', 0.35);
      });

      w.part('볼펜');
      // 갈피 표시. 몸통이 표지 밖으로 나와야 「여기를 다시 봤다」가 읽힌다
      w.movable('볼펜', () => {
        const barrel = new THREE.CylinderGeometry(0.0037, 0.0034, 0.128, 10);
        barrel.rotateX(Math.PI / 2);
        barrel.rotateY(0.11);
        barrel.translate(0.026, 0.0155, -0.014);
        paintGeometry(barrel, '#2c3038');
        w.add(barrel, 'plastic', 0.2);
        const tip = new THREE.ConeGeometry(0.0034, 0.011, 10);
        tip.rotateX(-Math.PI / 2);
        tip.rotateY(0.11);
        tip.translate(0.0187, 0.0155, -0.0825);
        w.add(tip, 'steel', 0.05);
        const clip = bevelBox(0.0044, 0.0016, 0.034, 0.0006);
        clip.rotateY(0.11);
        clip.translate(0.0288, 0.0196, 0.031);
        w.add(clip, 'steel', 0.05);
      });

      w.part('고무밴드');
      // 벗겨져 옆으로 밀려났다. 제자리에 있으면 「열어 본 적 없는 수첩」이다
      w.movable('고무밴드', () => {
        const band = spline(
          [
            [-0.0355, 0.0016, -d.d / 2 + 0.006],
            [-0.041, 0.0125, -0.03],
            [-0.0405, 0.0205, 0.012],
            [-0.036, 0.0125, 0.046],
            [-0.0345, 0.0016, d.d / 2 - 0.006],
          ],
          16,
        );
        ribbon(w, band, 0.0055, 0.0018, 'rubber');
      });

      w.part('표지 라벨');
      /* 라벨은 표지 위에 붙어 있으니 표지와 함께 넘어간다 — 같은 이름의 movable 버킷에
       * 다시 넣으면 한 부품으로 합쳐진다 */
      w.movable('앞표지', () => {
        w.thinSurface(() => {
          const p = facingUp(d.w * 0.5, d.d * 0.16);
          p.rotateX(-0.115);
          p.translate(0, 0.0195 + PEN_Y * 0.28, -0.03);
          w.add(p, 'painted', 0);
        });
      });
    },
    materials: () => ({
      painted: printedFace(
        (g, W, H) => {
          g.strokeStyle = 'rgba(216,212,200,0.62)';
          stroke(g, W * 0.12, H * 0.56, W * 0.62, H * 0.56, H * 0.1, 3);
        },
        192,
        64,
        '#4a3f36',
      ),
    }),
  },

  {
    id: 'power_logger',
    mount: 'wall',
    name: '전력기록계',
    collection: 'sahol',
    category: '단서',
    hangs: true,
    sunk: true,
    note:
      'E23. 명주원 작업실 배전반 옆 전동공구 전력기록계. **이 조각의 존재 이유는 화면 하나다** — ' +
      '24시간 막대그래프에서 22시대 두 칸이 바닥까지 꺼져 있고 23시 이후 다시 솟아야 한다. ' +
      '그 구멍이 안 보이면 카드가 성립하지 않는다. 그래서 막대는 아이콘이 아니라 ' +
      '**높이가 실제로 다른 24개**여야 하고, 꺼진 칸은 0 이 아니라 미세하게 남은 대기전력이어야 한다. ' +
      '벽에 붙는 물건이라 원점이 걸이 지점이고 몸통은 그 아래로 내려간다(hangs+sunk). ' +
      '상표 금지 — 계측기 특유의 각인 명판을 쓰지 않고 무지 패널에 단자 기호만 둔다.',
    dims: { w: 0.158, h: 0.122, d: 0.07 },
    clusters: 1,
    // 단자대 덮개를 아래로 젖혀 배선구를 연다 — 계측기 단자 커버
    openable: [
      {
        part: '단자 덮개',
        swing: 1.2,
        axis: 'x',
        pivot: [0, -0.088, -0.0085],
        inside: { w: 0.13, h: 0.028, d: 0.014, at: [0, -0.105, -0.001] },
      },
    ],
    build(w, d) {
      const Y0 = -d.h; // 몸통 바닥. 원점은 상단 걸이점이다

      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.d, 0.004, 2);
      body.translate(0, Y0 + d.h / 2, d.d / 2 - 0.004);
      paintGeometry(body, '#c9c6bd');
      w.add(body, 'plastic', 0.35);

      w.part('조작 패널');
      // 이름 규약상 -Z 를 봐야 한다. 화면이 앉는 우묵한 자리
      const bezel = bevelBox(d.w * 0.84, d.h * 0.6, 0.008, 0.0025);
      bezel.translate(0, Y0 + d.h * 0.6, -0.0035);
      paintGeometry(bezel, '#2b2f34');
      w.add(bezel, 'plastic', 0.25);

      w.part('화면');
      w.thinSurface(() => {
        const p = facingFront(d.w * 0.78, d.h * 0.54);
        p.translate(0, Y0 + d.h * 0.6, -0.0078);
        w.add(p, 'screen', 0);
      });

      w.part('단자대');
      // 아래쪽 배선구. 계측기는 아래로 선이 나온다
      const tb = bevelBox(d.w * 0.9, d.h * 0.2, 0.01, 0.002);
      tb.translate(0, Y0 + d.h * 0.14, -0.0035);
      paintGeometry(tb, '#8e8a80');
      w.add(tb, 'plastic', 0.25);
      for (let i = 0; i < 5; i++) {
        const scr = new THREE.CylinderGeometry(0.0032, 0.0032, 0.004, 8);
        scr.rotateX(Math.PI / 2);
        scr.translate(-d.w * 0.3 + i * (d.w * 0.15), Y0 + d.h * 0.14, -0.0085);
        w.add(scr, 'steel', 0.06);
      }

      w.part('단자 덮개');
      // 단자대를 가리는 얇은 판. 윗변 경첩으로 아래로 젖혀진다
      w.movable('단자 덮개', () => {
        const cov = bevelBox(d.w * 0.9, 0.034, 0.003, 0.001);
        cov.translate(0, -0.105, -0.01);
        paintGeometry(cov, '#b8b4a9');
        w.add(cov, 'plastic', 0.25);
      });

      w.part('케이블');
      // 배전반으로 내려가는 선. 없으면 벽에 붙인 액자다
      const cab = spline(
        [
          [0.018, Y0 + d.h * 0.06, 0.004],
          [0.03, Y0 - 0.05, 0.012],
          [0.012, Y0 - 0.115, 0.026],
          [-0.03, Y0 - 0.17, 0.02],
        ],
        16,
      );
      ribbon(w, cab, 0.0075, 0.0058, 'rubber');
      const gland = new THREE.CylinderGeometry(0.008, 0.0095, 0.016, 10);
      gland.translate(0.018, Y0 + d.h * 0.045, 0.004);
      w.add(gland, 'rubber', 0.1);

      w.part('고정 나사');
      for (const sx of [-1, 1]) {
        const scr = new THREE.CylinderGeometry(0.004, 0.004, 0.005, 8);
        scr.rotateX(Math.PI / 2);
        scr.translate(sx * (d.w / 2 - 0.011), Y0 + d.h - 0.011, -0.0035);
        w.add(scr, 'steel', 0.06);
      }
    },
    materials: () => ({
      screen: litFace(
        (g, W, H) => {
          const padL = W * 0.06;
          const padR = W * 0.04;
          const base = H * 0.86;
          const top = H * 0.34;
          const bw = (W - padL - padR) / 24;
          // 24시간 부하. 22·23시 자리가 바닥까지 꺼져 있는 것이 E23 의 전부다
          const load = [
            0.06, 0.05, 0.05, 0.05, 0.05, 0.06, 0.12, 0.3, 0.62, 0.78, 0.71, 0.55,
            0.34, 0.66, 0.83, 0.75, 0.69, 0.58, 0.44, 0.52, 0.61, 0.48, 0.04, 0.05,
          ];
          // 23시 이후 재활동 — 배열 끝 두 칸이 꺼진 뒤 다음 날 0시가 다시 솟는다
          load[22] = 0.035;
          load[23] = 0.045;
          for (let i = 0; i < 24; i++) {
            const hgt = (base - top) * load[i];
            const dead = load[i] < 0.09;
            g.fillStyle = dead ? 'rgba(120,150,170,0.32)' : '#7fd6b0';
            g.fillRect(padL + i * bw + bw * 0.16, base - hgt, bw * 0.68, hgt);
          }
          // 꺼진 구간을 붉게 감싼다 — 기록계가 스스로 이상을 표시하고 있다
          g.strokeStyle = 'rgba(226,96,84,0.85)';
          g.lineWidth = H * 0.012;
          g.strokeRect(padL + 22 * bw, top - H * 0.03, bw * 2, base - top + H * 0.06);
          // 바닥 눈금
          g.fillStyle = 'rgba(150,180,200,0.5)';
          g.fillRect(padL, base, W - padL - padR, H * 0.008);
          // 현재값 7세그
          const dg = '0028';
          for (let i = 0; i < dg.length; i++) {
            seg7(g, dg[i], padL + i * (W * 0.062), H * 0.07, W * 0.05, H * 0.2, W * 0.009);
          }
        },
        512,
        320,
      ),
    }),
  },

  {
    id: 'case_board',
    name: '수사자료 보드',
    collection: 'sahol',
    category: '단서',
    note:
      'E04. 조합센터 임시수사실의 정우빈 수사자료 보드. R3 전환에서 자동공개되는 물건이라 ' +
      '**들어서는 순간 「수사가 시작됐다」가 읽혀야 한다.** ' +
      '빈 코르크판을 놓으면 아무 일도 일어나지 않는다 — 사진이 여러 장 압정으로 박혀 있고 ' +
      '그 사이를 붉은 실이 건너다녀야 한다. 실이 이 조각의 정체다. ' +
      '사진은 아틀라스 열두 칸을 나눠 써서 장마다 다른 것이 찍혀 있어야 한다. 같은 그림 열두 장은 ' +
      '복사-붙여넣기가 눈에 보인다. 이젤 다리는 접이식이라 뒤로 벌어지고, 아래 트레이에 ' +
      '마커와 압정통이 굴러 있다.',
    dims: { w: 1.16, h: 1.68, d: 0.52 },
    clusters: 1,
    /* 사진을 압정에서 들어 앞으로 당겨 볼 수 있고(실은 판에 박힌 채 남는다),
     * 트레이의 마커와 압정통을 집을 수 있다 */
    openable: [
      { part: '사진 1', slide: [0, 0, -0.025] },
      { part: '사진 2', slide: [0, 0, -0.025] },
      { part: '사진 3', slide: [0, 0, -0.025] },
      { part: '사진 4', slide: [0, 0, -0.025] },
      { part: '사진 5', slide: [0, 0, -0.025] },
      { part: '사진 6', slide: [0, 0, -0.025] },
      { part: '사진 7', slide: [0, 0, -0.025] },
      { part: '사진 8', slide: [0, 0, -0.025] },
      { part: '사진 9', slide: [0, 0, -0.025] },
      { part: '사진 10', slide: [0, 0, -0.025] },
      { part: '사진 11', slide: [0, 0, -0.025] },
      { part: '사진 12', slide: [0, 0, -0.025] },
      { part: '마커 1', slide: [0, 0.04, 0] },
      { part: '마커 2', slide: [0, 0.04, 0] },
      { part: '압정통', slide: [0, 0.04, 0] },
    ],
    build(w, d) {
      const BW = d.w;
      const BH = 0.86; // 판 높이
      const BY = d.h - BH; // 판 아랫변
      const BZ = 0.03; // 판 앞면 z

      w.part('보드판');
      const panel = bevelBox(BW, BH, 0.026, 0.004);
      panel.translate(0, BY + BH / 2, BZ);
      paintGeometry(panel, '#b8956a');
      w.add(panel, 'cardboard', 0.9);

      w.part('테두리');
      // 알루미늄 프레임. 코르크만 두면 두꺼운 골판지로 보인다
      for (const [fw, fh, fx, fy] of [
        [BW + 0.03, 0.028, 0, BY + BH + 0.007],
        [BW + 0.03, 0.028, 0, BY - 0.007],
        [0.028, BH + 0.042, -(BW / 2 + 0.001), BY + BH / 2],
        [0.028, BH + 0.042, BW / 2 + 0.001, BY + BH / 2],
      ] as const) {
        const f = bevelBox(fw, fh, 0.034, 0.003);
        f.translate(fx, fy, BZ);
        w.add(f, 'alu', 0.3);
      }

      w.part('다리');
      // 접이식 이젤. 앞다리는 판을 따라 서고, **뒷다리는 판 윗부분에 경첩으로 물려**
      // 뒤로 벌어진다. 뒷다리를 아래에서 세우면 판까지 닿지 않아 공중에 뜬다 —
      // 회전각으로 맞추려 들지 말고 위·아래 끝점을 직접 찍는 편이 확실하다
      const REAR_TOP = 1.3;
      const REAR_Z0 = BZ + 0.02;
      const REAR_Z1 = 0.33;
      for (const sx of [-1, 1]) {
        const front = bevelBox(0.034, d.h * 0.62, 0.03, 0.0035);
        front.rotateZ(sx * 0.045);
        front.translate(sx * (BW / 2 - 0.06), (d.h * 0.62) / 2, BZ);
        w.add(front, 'steel', 0.25);
        ribbon(
          w,
          [
            new THREE.Vector3(sx * 0.44, REAR_TOP, REAR_Z0),
            new THREE.Vector3(sx * 0.47, 0.016, REAR_Z1),
          ],
          0.032,
          0.028,
          'steel',
        );
      }
      // 가로 버팀대 — 없으면 다리 넷이 각자 서 있는 것으로 보인다.
      // 뒷다리가 뒤로 벌어진 만큼 z 를 따라가야 실제로 닿는다.
      //
      // **길이를 넉넉히 잡아 다리를 뚫고 지나가게 두면 안 된다.** joint-check 의
      // 표면거리는 삼각형의 **꼭짓점·중심**만 표본으로 쓴다 — 긴 상자 둘이 교차해도
      // 교차점 근처에 정점이 하나도 없으면 양끝 모서리끼리의 거리(49mm)가 나온다.
      // 그래서 버팀대를 **다리 축에서 10mm 만 더 들어간 곳에서 끊어** 끝면 정점이
      // 다리 안에 놓이게 한다. 단면도 다리(32x28)보다 작아야 정점이 밖으로 안 샌다
      const braceY = 0.35;
      const braceT = (REAR_TOP - braceY) / (REAR_TOP - 0.016);
      const braceZ = REAR_Z0 + (REAR_Z1 - REAR_Z0) * braceT;
      const braceX = 0.44 + 0.03 * braceT + 0.01;
      const brace = bevelBox(braceX * 2, 0.022, 0.018, 0.003);
      brace.translate(0, braceY, braceZ);
      w.add(brace, 'steel', 0.25);

      w.part('트레이');
      const tray = bevelBox(BW * 0.88, 0.016, 0.086, 0.004);
      tray.translate(0, BY - 0.03, BZ - 0.038);
      w.add(tray, 'alu', 0.3);
      const lip = bevelBox(BW * 0.88, 0.026, 0.012, 0.003);
      lip.translate(0, BY - 0.018, BZ - 0.077);
      w.add(lip, 'alu', 0.3);

      w.part('마커');
      for (let i = 0; i < 2; i++) {
        w.movable('마커 ' + (i + 1), () => {
          const mk = new THREE.CylinderGeometry(0.0085, 0.0085, 0.125, 10);
          mk.rotateZ(Math.PI / 2);
          mk.rotateY((rnd(i * 5) - 0.5) * 0.14);
          mk.translate(-0.28 + i * 0.1, BY - 0.013, BZ - 0.05 + rnd(i * 3) * 0.012);
          paintGeometry(mk, i === 0 ? '#8c2b26' : '#23262b');
          w.add(mk, 'plastic', 0.2);
        });
      }

      w.part('압정통');
      w.movable('압정통', () => {
        const tin = new THREE.CylinderGeometry(0.023, 0.023, 0.02, 14);
        tin.rotateZ(1.5);
        tin.translate(0.3, BY - 0.012, BZ - 0.05);
        w.add(tin, 'alu', 0.15);
      });

      // 사진과 쪽지 — 자리를 먼저 정하고 실이 그 자리를 잇는다
      const PIN: [number, number, number, number][] = [
        // [x, y(판 하단 기준 비율), 폭, 아틀라스 인덱스]
        [-0.42, 0.74, 0.148, 0],
        [-0.16, 0.79, 0.132, 1],
        [0.12, 0.75, 0.146, 2],
        [0.4, 0.8, 0.13, 3],
        [-0.46, 0.45, 0.134, 4],
        [-0.18, 0.48, 0.15, 5],
        [0.14, 0.44, 0.132, 6],
        [0.43, 0.49, 0.144, 7],
        [-0.36, 0.16, 0.128, 8],
        [-0.04, 0.19, 0.142, 9],
        [0.26, 0.15, 0.136, 10],
        [0.47, 0.2, 0.12, 11],
      ];

      w.part('사진');
      PIN.forEach(([px, pv, pw, idx], i) => {
        w.movable('사진 ' + (i + 1), () =>
          w.thinSurface(() => {
            const ph = pw * 0.72;
            const p = facingFront(pw, ph);
            p.rotateZ((rnd(i * 13) - 0.5) * 0.13); // 삐뚜름해야 사람이 꽂은 것이다
            p.translate(px, BY + BH * pv, BZ - 0.0145 - (i % 3) * 0.0004);
            atlasUV(p, idx % 4, Math.floor(idx / 4), 4, 3);
            w.add(p, 'painted', 0);
          }),
        );
      });

      w.part('압정');
      PIN.forEach(([px, pv, pw], i) => {
        const ph = pw * 0.72;
        const head = new THREE.CylinderGeometry(0.0058, 0.0052, 0.005, 8);
        head.rotateX(Math.PI / 2);
        head.translate(px + (rnd(i * 7) - 0.5) * 0.012, BY + BH * pv + ph * 0.42, BZ - 0.019);
        paintGeometry(head, i % 3 === 0 ? '#c8443a' : i % 3 === 1 ? '#2f6ea8' : '#d8b23c');
        w.add(head, 'plastic', 0.1);
      });

      w.part('연결 실');
      // 이 조각의 정체. 사진 사이를 건너다니며 살짝 처진다
      const link: [number, number][] = [
        [0, 5],
        [5, 1],
        [1, 9],
        [9, 6],
        [6, 2],
        [2, 7],
        [7, 10],
        [4, 8],
        [3, 11],
      ];
      for (const [a, b] of link) {
        const pa = PIN[a];
        const pb = PIN[b];
        const ya = BY + BH * pa[1] + pa[2] * 0.72 * 0.42;
        const yb = BY + BH * pb[1] + pb[2] * 0.72 * 0.42;
        const pts = spline(
          [
            [pa[0], ya, BZ - 0.021],
            [(pa[0] + pb[0]) / 2, (ya + yb) / 2 - 0.012, BZ - 0.024],
            [pb[0], yb, BZ - 0.021],
          ],
          7,
        );
        ribbon(w, pts, 0.0022, 0.0016, 'fabric');
      }
    },
    materials: (renderer) => {
      const anis = renderer.capabilities.getMaxAnisotropy?.() ?? 4;
      const mat = printedFace(
        (g, W, H) => {
          // 4x3 아틀라스. 칸마다 다른 것이 찍혀 있어야 열두 장이 열두 장으로 읽힌다
          const cw = W / 4;
          const ch = H / 3;
          for (let r = 0; r < 3; r++) {
            for (let c = 0; c < 4; c++) {
              const i = r * 4 + c;
              const ox = c * cw;
              const oy = r * ch;
              // 인화지 흰 테두리
              g.fillStyle = '#efeae0';
              g.fillRect(ox + 2, oy + 2, cw - 4, ch - 4);
              const ix = ox + cw * 0.07;
              const iy = oy + ch * 0.07;
              const iw = cw * 0.86;
              const ih = ch * 0.74;
              // 현장사진은 색이 빠져 있다 — 형광등 밑 실내 야간촬영
              const tone = ['#3e4650', '#4a4238', '#39423c', '#4b4550'][i % 4];
              g.fillStyle = tone;
              g.fillRect(ix, iy, iw, ih);
              // 밝은 덩어리 두엇 — 어디를 찍었는지가 실루엣으로만 남는다
              for (let k = 0; k < 3; k++) {
                const s = rnd(i * 31 + k * 7);
                g.fillStyle = `rgba(210,206,196,${0.1 + s * 0.22})`;
                g.fillRect(
                  ix + iw * (0.08 + rnd(i * 11 + k) * 0.6),
                  iy + ih * (0.1 + rnd(i * 17 + k) * 0.6),
                  iw * (0.12 + s * 0.3),
                  ih * (0.1 + rnd(i * 23 + k) * 0.34),
                );
              }
              // 플래시 반사 한 점
              g.fillStyle = 'rgba(255,252,242,0.5)';
              g.beginPath();
              g.arc(ix + iw * (0.2 + rnd(i * 41) * 0.6), iy + ih * (0.2 + rnd(i * 43) * 0.5), iw * 0.05, 0, Math.PI * 2);
              g.fill();
              // 아래 여백의 메모 두 줄
              g.strokeStyle = 'rgba(40,46,60,0.7)';
              stroke(g, ix, oy + ch * 0.87, ix + iw * (0.3 + rnd(i * 5) * 0.5), oy + ch * 0.87, ch * 0.022, i);
            }
          }
        },
        1024,
        768,
        '#efeae0',
      );
      if (mat.map) mat.map.anisotropy = anis;
      return { painted: mat };
    },
  },
/* 사흘 v8 5단계 — A8 부츠 3종 + A12 가방 3종 초안.
 * saholPersonal.ts 의 SAHOL_PERSONAL_PIECES 끝에 붙는다.
 * 공통 규약: 원점 바닥 한가운데, 정면 -Z, 미터.
 * 부츠는 「한 짝은 서 있고 한 짝은 쓰러져 밑창이 보인다」 — rotX(+π/2) 로
 * 등을 대고 눕히면 밑창(-Y)이 -Z(정면)를 본다.
 */

// ── 부츠 공통: 한 켤레 중 눕힌 쪽이 밑창을 보인다 ──
// upright:  서 있는 부츠 (px,pz)
// tipped:   등을 대고 누운 부츠 — 밑창이 정면(-Z)을 본다

  {
    id: 'boots_padded',
    nofront: true,
    name: '방한부츠 한 켤레',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A8 — 배정호(bj_util)의 방한부츠 275. 눈밭용이라 퀼팅 같은 패딩 ' +
      '주름이 잡힌 두꺼운 통에 털 달린 깃이 둘린다. 작업화보다 훨씬 부피가 크고 ' +
      '밑창은 깊은 러그. 한 짝이 옆으로 쓰러져 밑창이 보인다 — 「밑창 무늬가 ' +
      '굵다」는 V17 언덕길 발자국과 대조하는 재료다.',
    dims: { w: 0.4, h: 0.34, d: 0.36 },
    build(w) {
      const LUG = '#26221e', PAD = '#4a4038', FUR = '#8d8074';
      /* 한 짝 지오메트리 — upright 면 그대로, tipped 면 호출부에서 돌린다.
       * 눕힌 부츠는 밑창 뒤꿈치 끝(로컬 z 최대 ≈0.15)이 바닥에 닿으므로
       * 리프트는 반깊이만큼 필요하다. */
      const LIFT = 0.149;
      const boot = (px: number, pz: number, yaw: number, tip: boolean) => {
        const g = (geo: THREE.BufferGeometry, x: number, y: number, z: number, c: string, slot: 'rubber' | 'fabric') => {
          paintGeometry(geo, c);
          geo.translate(x, y, z);
          if (tip) { geo.rotateX(Math.PI / 2 - 0.1); }
          geo.rotateY(yaw);
          geo.translate(px, tip ? LIFT : 0, pz);
          w.add(geo, slot, 0.7);
        };
        // 밑창 — 두툼하고 아래에 러그 블록
        g(bevelBox(0.15, 0.03, 0.3, 0.006), 0, 0.02, 0, LUG, 'rubber');
        for (let i = 0; i < 5; i++) {
          g(bevelBox(0.14, 0.012, 0.038, 0.003), 0, -0.004, -0.1 + i * 0.05, '#1a1713', 'rubber');
        }
        // 발등·발가락
        g(bevelBox(0.13, 0.085, 0.27, 0.022), 0, 0.075, -0.005, PAD, 'fabric');
        // 통 — 패딩 주름(가로 밴드 3줄)이 방한부츠의 실루엣
        g(bevelBox(0.16, 0.22, 0.17, 0.025), 0, 0.215, 0.055, PAD, 'fabric');
        for (const y of [0.16, 0.225, 0.29]) {
          g(bevelBox(0.166, 0.018, 0.176, 0.006), 0, y, 0.055, '#3a332b', 'fabric');
        }
        // 깃 — 털 테두리
        g(bevelBox(0.175, 0.045, 0.185, 0.02), 0, 0.315, 0.055, FUR, 'fabric');
      };
      boot(-0.1, 0, 0.15, false);
      boot(0.12, 0.02, -0.35, true);
    },
  },

  {
    id: 'boots_work',
    nofront: true,
    name: '작업화 한 켤레',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A8 — 명주원(mj_shop)의 작업화 280. 발목까지만 오는 중간 목에 ' +
      '강철 토캡 부푼 앞코, 힐과 앞창이 갈라진 클래식한 작업화 밑창. ' +
      '레이스 후크(끈 고리)가 앞면에 두 줄로 돌출한다 — 장화와 구분되는 디테일.',
    dims: { w: 0.36, h: 0.22, d: 0.34 },
    build(w) {
      const LEATHER = '#4f3d2a', SOLE = '#2e2418', WELT = '#6b5638';
      const LIFT = 0.14;
      const boot = (px: number, pz: number, yaw: number, tip: boolean) => {
        const g = (geo: THREE.BufferGeometry, x: number, y: number, z: number, c: string, slot: 'rubber' | 'leather' | 'steel') => {
          paintGeometry(geo, c);
          geo.translate(x, y, z);
          if (tip) { geo.rotateX(Math.PI / 2 - 0.1); }
          geo.rotateY(yaw);
          geo.translate(px, tip ? LIFT : 0, pz);
          w.add(geo, slot, 0.7);
        };
        // 힐 + 앞창 — 갈라진 밑창이 작업화의 밑면 실루엣
        g(bevelBox(0.135, 0.04, 0.1, 0.006), 0, 0.025, 0.09, SOLE, 'rubber');
        g(bevelBox(0.135, 0.028, 0.19, 0.006), 0, 0.018, -0.055, SOLE, 'rubber');
        // 웰트(창과 갑피 사이 테두리) — 색이 다른 한 줄
        g(bevelBox(0.14, 0.014, 0.29, 0.005), 0, 0.048, 0, WELT, 'leather');
        // 갑피 + 강철 토캡
        g(bevelBox(0.125, 0.075, 0.26, 0.02), 0, 0.09, -0.005, LEATHER, 'leather');
        g(bevelBox(0.115, 0.05, 0.07, 0.018), 0, 0.075, -0.115, '#5d4a33', 'leather');
        // 발목 통 (낮다)
        g(bevelBox(0.12, 0.1, 0.14, 0.02), 0, 0.14, 0.05, LEATHER, 'leather');
        // 레이스 후크 4쌍 — 앞면(-Z)에 좌우 돌출
        for (let i = 0; i < 4; i++) {
          for (const sx of [-1, 1]) {
            const hook = bevelBox(0.012, 0.008, 0.008, 0.002);
            hook.translate(sx * 0.035, 0.1 + i * 0.024, -0.055 + i * 0.014);
            paintGeometry(hook, '#8f8f96');
            if (tip) hook.rotateX(Math.PI / 2 - 0.1);
            hook.rotateY(yaw);
            hook.translate(px, tip ? LIFT : 0, pz);
            w.add(hook, 'steel', 0.4);
          }
        }
      };
      boot(-0.09, 0, -0.1, false);
      boot(0.11, 0.03, 0.4, true);
    },
  },

  {
    id: 'boots_rubber',
    nofront: true,
    name: '고무장화 한 켤레',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A8 — 차유리(cy_roast)의 로스팅용 고무장화 270. 이음새 없이 ' +
      '미끈하게 올라가는 장화 — 끈도 후크도 없다. 밑창은 얕고 고르다. ' +
      '부츠 중 가장 키가 크고 가장 매끈한 실루엣.',
    dims: { w: 0.36, h: 0.36, d: 0.34 },
    build(w) {
      const RUB = '#25333a', SOLE = '#1b262c';
      const LIFT = 0.142;
      const boot = (px: number, pz: number, yaw: number, tip: boolean) => {
        const g = (geo: THREE.BufferGeometry, x: number, y: number, z: number, c: string) => {
          paintGeometry(geo, c);
          geo.translate(x, y, z);
          if (tip) { geo.rotateX(Math.PI / 2 - 0.1); }
          geo.rotateY(yaw);
          geo.translate(px, tip ? LIFT : 0, pz);
          w.add(geo, 'rubber', 0.5);
        };
        // 얕은 밑창 — 장화는 힐이 거의 없다
        g(bevelBox(0.135, 0.022, 0.285, 0.006), 0, 0.014, 0, SOLE);
        // 얕은 체브론 트레드 — 밑창 아래 비스듬한 얇은 줄
        for (let i = 0; i < 6; i++) {
          const t = bevelBox(0.05, 0.008, 0.014, 0.002);
          t.rotateY(i % 2 ? 0.5 : -0.5);
          g(t, i % 2 ? -0.028 : 0.028, -0.002, -0.1 + i * 0.04, '#141d22');
        }
        // 갑피 — 굽 없이 밑창에서 바로 오른다
        g(bevelBox(0.115, 0.07, 0.26, 0.024), 0, 0.06, -0.008, RUB);
        // 통 — 길고 매끈, 위로 갈수록 아주 조금 넓어짐
        const shaft = bevelBox(0.135, 0.28, 0.15, 0.03);
        shaft.translate(0, 0.2, 0.045);
        paintGeometry(shaft, RUB);
        if (tip) shaft.rotateX(Math.PI / 2 - 0.1);
        shaft.rotateY(yaw);
        shaft.translate(px, tip ? LIFT : 0, pz);
        w.add(shaft, 'rubber', 0.45);
        // 입구 롤테두리 한 줄
        g(bevelBox(0.145, 0.02, 0.16, 0.008), 0, 0.335, 0.045, '#1e2a30');
      };
      boot(-0.1, 0, 0.1, false);
      boot(0.12, 0.02, -0.3, true);
    },
  },

  /* ── A12 가방 — 회의실 라인업의 비교재료. 실루엣이 전부 달라야 한다 ── */

  {
    id: 'tool_bag',
    nofront: true,
    name: '공구가방',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A12 — 명주원의 공구가방(회의실 가방 라인업 비교재료). 위가 ' +
      '활짝 벌어진 직사각 통가방 — 덮개가 없고 입구가 벌어져 있어야 공구가방이다. ' +
      '손잡이 둘이 위에서 교차하고, 렌치 손잡이 같은 공구가 입구 밖으로 삐져나온다. ' +
      '앞면에 덮개 주머니 하나 — 덮개를 젖히면 줄자·연필이 보인다.',
    dims: { w: 0.42, h: 0.28, d: 0.2 },
    /* **앞주머니 덮개를 젖힌다** (2026-09-23, Chris: 「서랍 고치고 ㄱㄱ」 — 회의실 가방 셋만 안 열렸다).
     * 위가 벌어진 통이라 입구로는 「열」 것이 없다. 공구가방 앞의 덮개 주머니가 실물에서도 줄자·연필 자리다.
     * 몸통을 뒤로 PD 만큼 물려 주머니까지 합친 깊이가 dims.d 안에 든다. 축 좌표는 정적이라 dims 를 못 읽는다:
     * 몸통 앞면 z = -d.d/2 + PD = -0.065 · 주머니 윗면 y = 0.14 · 덮개 윗면 y = 0.145 */
    openable: [
      { part: '앞주머니', swing: 1.5, axis: 'x', pivot: [0, 0.145, -0.065],
        inside: { w: 0.2, h: 0.08, d: 0.018, at: [0, 0.1, -0.076] } },
    ],
    build(w, d) {
      const CANVAS = '#5c5240';
      const BT = 0.014, bh = d.h * 0.72;
      const PD = 0.035, BD = d.d - PD, zc = PD / 2;   // 주머니 깊이 · 몸통 깊이 · 몸통 중심 z
      // 벽 넷 + 바닥 — 위는 열려 있다
      const wall = (ww: number, dd: number, px: number, pz: number) => {
        const g = bevelBox(ww, bh, dd, 0.006, 2);
        g.translate(px, bh / 2 + 0.01, pz);
        paintGeometry(g, CANVAS);
        w.add(g, 'fabric', 0.65);
      };
      w.part('몸통');
      wall(d.w, BT, 0, zc - BD / 2 + BT / 2);
      wall(d.w, BT, 0, zc + BD / 2 - BT / 2);
      wall(BT, BD - BT * 2, -d.w / 2 + BT / 2, zc);
      wall(BT, BD - BT * 2, d.w / 2 - BT / 2, zc);
      const base = bevelBox(d.w, BT, BD, 0.006, 2);
      base.translate(0, 0.01 + BT / 2, zc);
      paintGeometry(base, '#4a4234');
      w.add(base, 'fabric', 0.65);
      // 입구 안쪽 — 어두운 내부가 보여야 「열린 통」이다
      const mouth = bevelBox(d.w - BT * 2.4, 0.01, BD - BT * 2.4, 0.002);
      mouth.translate(0, bh - 0.03, zc);
      paintGeometry(mouth, '#16130e');
      w.add(mouth, 'fabric', 0.9);
      // 입구 테두리 밴드
      const rim = bevelBox(d.w + 0.008, 0.025, BD + 0.008, 0.006);
      rim.translate(0, bh + 0.005, zc);
      paintGeometry(rim, '#4a4234');
      w.add(rim, 'fabric', 0.6);

      w.part('앞주머니');
      // 벽 셋 + 바닥, 뒤는 몸통 앞면. 속이 비어야 덮개를 젖혔을 때 안이 보인다
      const PW = 0.24, PT = 0.006, py0 = 0.05, py1 = 0.14, pz0 = -d.d / 2 + 0.007, pz1 = zc - BD / 2;
      const pocket = (ww: number, hh: number, dd: number, px: number, pyc: number, pz: number) => {
        const g = bevelBox(ww, hh, dd, 0.002, 1);
        g.translate(px, pyc, pz);
        paintGeometry(g, '#544a39');
        w.add(g, 'fabric', 0.6);
      };
      pocket(PW, py1 - py0, PT, 0, (py0 + py1) / 2, pz0 + PT / 2);
      for (const sx of [-1, 1]) pocket(PT, py1 - py0, pz1 - pz0 - PT, sx * (PW / 2 - PT / 2), (py0 + py1) / 2, (pz0 + PT + pz1) / 2);
      pocket(PW - PT * 2, PT, pz1 - pz0 - PT, 0, py0 + PT / 2, (pz0 + PT + pz1) / 2);

      w.part('주머니 속');
      // 줄자와 몽당연필 — 덮개를 젖혔을 때 위만 보인다. 주머니 바닥에 선다
      const pin = (pz0 + PT + pz1) / 2;
      const tape = bevelBox(0.058, 0.058, 0.016, 0.006, 2);
      tape.translate(-0.05, py0 + PT + 0.029, pin);
      paintGeometry(tape, '#b8932a');
      w.add(tape, 'plastic', 0.2);
      const pencil = bevelBox(0.008, 0.075, 0.008, 0.002);
      pencil.rotateZ(0.12);
      pencil.translate(0.05, py0 + PT + 0.0375, pin);
      paintGeometry(pencil, '#c98a3a');
      w.add(pencil, 'plastic', 0.1);

      w.movable('앞주머니', () => {
        /* 윗판이 주머니 입구를 덮고, 가운데 똑딱이 탭 하나만 앞으로 늘어진다 — 뒤 모서리(몸통 앞면)에서 위로 젖혀진다.
         * 폭 전체에 자락을 달았더니 젖혔을 때 자락이 수평으로 서서 주머니 입구를 위에서 통째로 가렸다(2026-09-23 열림 컷).
         * 탭은 가운데 3cm 라 젖혀도 양옆의 줄자·연필이 보인다 */
        const lid = bevelBox(PW + 0.01, 0.005, pz1 - pz0 + 0.002, 0.002, 1);
        lid.translate(0, py1 + 0.0025, (pz0 + pz1) / 2 - 0.001);
        paintGeometry(lid, '#4a4234');
        w.add(lid, 'fabric', 0.6);
        const tab = bevelBox(0.03, 0.03, 0.004, 0.0015, 1);
        tab.translate(0, py1 + 0.005 - 0.015, pz0 - 0.002);
        paintGeometry(tab, '#3d362a');
        w.add(tab, 'leather', 0.3);
      });

      w.part('손잡이');
      // 두 손잡이가 위에서 X 로 엇갈린다 — 공구가방 특유의 실루엣
      for (const s of [-1, 1]) {
        const h = bevelBox(0.02, 0.1, 0.012, 0.004);
        h.rotateX(s * 0.5);
        h.translate(0, bh + 0.06, zc + s * (BD / 2 - 0.02));
        paintGeometry(h, '#3d362a');
        w.add(h, 'leather', 0.6);
      }

      w.part('공구');
      // 입구 밖으로 삐져나온 공구 손잡이들 — 이게 없으면 그냥 토트다
      const tools: [number, number, number, number, string][] = [
        [-0.12, 0.05, 0.06, 0.3, '#8f8f96'],   // 렌치
        [0.02, -0.04, -0.05, -0.25, '#9c3b30'], // 드라이버(적색 손잡이)
        [0.13, 0.02, 0.03, 0.15, '#8f8f96'],
      ];
      for (const [tx, tz, tilt, ry, c] of tools) {
        const t = bevelBox(0.018, 0.14, 0.014, 0.003);
        t.translate(0, 0.07, 0);
        t.rotateZ(tilt);
        t.rotateY(ry);
        t.translate(tx, bh - 0.02, tz + zc);
        paintGeometry(t, c);
        w.add(t, 'steel', 0.5);
      }
    },
  },

  {
    id: 'canvas_tote',
    nofront: true,
    name: '캔버스 토트백',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A12 — 차유리의 캔버스 토트(회의실 라인업 비교재료). 세로로 길고 ' +
      '얇은 직사각 — 손잡이 두 줄이 길게 서 있고, 책 한 권 모서리가 입구 밖으로 ' +
      '삐져나온다. 공구가방(낮고 넓음)·에코백(주저앉음)과 실루엣이 다르다. ' +
      '앞판 윗자락은 힘없는 천이라 손으로 젖히면 앞으로 벌어져 속이 보인다.',
    dims: { w: 0.36, h: 0.4, d: 0.11 },
    /* **앞판 윗자락이 앞으로 벌어진다** (2026-09-23, Chris: 「서랍 고치고 ㄱㄱ」 — 회의실 가방 셋만 안 열렸다).
     * 토트는 덮개도 지퍼도 없는 통이라, 연다는 건 힘없는 캔버스 입구를 손으로 벌려 들여다보는 것이다.
     * 앞판을 아래(고정)·윗자락(움직임) 둘로 나누고 앞 손잡이는 윗자락에 꿰매져 같이 넘어간다.
     * 축 좌표는 정적이라 dims 를 못 읽는다: 접히는 선 y = 0.005 + 0.312 - 0.1 = 0.217 · 앞판 z = -0.051 */
    openable: [
      { part: '입구', swing: -0.55, axis: 'x', pivot: [0, 0.217, -0.051],
        inside: { w: 0.32, h: 0.26, d: 0.08, at: [0, 0.145, 0] } },
    ],
    build(w, d) {
      const CANVAS = '#cfc4a8';
      const BT = 0.008, bh = d.h * 0.78, FB = 0.1;   // FB = 앞판에서 벌어지는 윗자락 높이
      w.part('몸통');
      // 얇은 앞·뒤 판 + 옆면 — 토트는 판판한 통이다
      const plate = (ww: number, dd: number, px: number, pz: number, hh = bh, y0 = 0.005) => {
        const g = bevelBox(ww, hh, dd, 0.004, 2);
        g.translate(px, y0 + hh / 2, pz);
        paintGeometry(g, CANVAS);
        w.add(g, 'fabric', 0.75);
      };
      plate(d.w, BT, 0, -d.d / 2 + BT / 2, bh - FB);
      plate(d.w, BT, 0, d.d / 2 - BT / 2);
      plate(BT, d.d - BT * 2, -d.w / 2 + BT / 2, 0);
      plate(BT, d.d - BT * 2, d.w / 2 - BT / 2, 0);
      const base = bevelBox(d.w, BT, d.d, 0.004);
      base.translate(0, 0.005 + BT / 2, 0);
      paintGeometry(base, '#b8ad90');
      w.add(base, 'fabric', 0.7);
      // 바닥·옆 박음질 라인 — 직조 가방의 읽힘 재료
      for (const pz of [-d.d / 2 - 0.001, d.d / 2 + 0.001]) {
        const seam = bevelBox(d.w * 0.94, 0.006, 0.002, 0.001);
        seam.translate(0, 0.045, pz);
        paintGeometry(seam, '#a89c7e');
        w.add(seam, 'fabric', 0.5);
      }

      w.part('속');
      // 바닥에 누운 천 파우치 — 윗자락을 벌리면 책 옆으로 보인다
      const pouch = bevelBox(0.15, 0.045, 0.06, 0.012, 2);
      pouch.translate(0.08, 0.005 + BT + 0.0225, 0.006);
      paintGeometry(pouch, '#5f6f62');
      w.add(pouch, 'fabric', 0.4);

      w.part('내용물');
      // 책 한 권 모서리가 입구 위로 삐져나옴 — 토트에 책·서류를 넣어 다니는 습관
      const book = bevelBox(0.2, 0.1, 0.026, 0.003);
      book.rotateZ(0.12);
      book.rotateY(0.15);
      book.translate(-0.03, bh + 0.02, 0.005);
      paintGeometry(book, '#7a3f35');
      w.add(book, 'paper', 0.6);

      w.part('손잡이');
      // 길고 가는 손잡이 두 줄 — 몸통보다 높이 솟아야 토트 실루엣. 앞줄은 윗자락과 같이 넘어간다
      const handles = (s: number) => {
        for (const sx of [-1, 1]) {
          const h = bevelBox(0.016, 0.16, 0.008, 0.003);
          h.rotateZ(sx * 0.06);
          h.translate(sx * d.w * 0.3, bh + 0.075, s * (d.d / 2 - 0.008));
          paintGeometry(h, '#8d8266');
          w.add(h, 'fabric', 0.6);
        }
      };
      handles(1);
      w.movable('입구', () => {
        w.part('앞 윗자락');
        plate(d.w, BT, 0, -d.d / 2 + BT / 2, FB, 0.005 + bh - FB);
        w.part('손잡이');
        handles(-1);
      });
    },
  },

  {
    id: 'eco_bag',
    nofront: true,
    name: '에코백 (주저앉은)',
    collection: 'sahol',
    category: '단서',
    note:
      '사흘 v8 A12 — 윤가람의 에코백(회의실 라인업 비교재료). 내용물을 다 뺀 ' +
      '얇은 천가방은 서 있지 못하고 바닥에 주저앉는다 — 납작하게 꺼진 몸체와 ' +
      '그 위에 얹힌 손잡이 끈. 「던져 두고 간 빈 가방」이 이 형태다. ' +
      '윗자락을 들추면 속에 접힌 비닐봉지만 깔려 있다.',
    dims: { w: 0.34, h: 0.14, d: 0.28 },
    /* **꺼진 윗자락을 들춘다** (2026-09-23, Chris: 「서랍 고치고 ㄱㄱ」 — 회의실 가방 셋만 안 열렸다).
     * 주저앉은 천가방은 입구가 따로 없다 — 눌린 위쪽 천을 뒤 모서리에서 들어 올려야 속이 보인다.
     * 아래 한 겹(바닥)만 두고 위 두 겹 + 끈을 같이 든다. 축 좌표는 정적이라 dims 를 못 읽는다:
     * 바닥 겹 윗면 y = 0.028 + 0.0225 = 0.05 · 뒤 모서리 z = 0.11 */
    openable: [
      { part: '윗자락', swing: 0.9, axis: 'x', pivot: [0, 0.05, 0.11],
        inside: { w: 0.26, h: 0.04, d: 0.18, at: [0, 0.052, 0] } },
    ],
    build(w, d) {
      const CLOTH = '#d8d2c0';
      w.part('몸통');
      /* 납작하게 꺼진 주름 덩어리 — 통이 아니라 눌린 천이다. 가로로 긴 납작
       * 베벌 상자 둘을 어긋나게 포개 꺼진 부피를 만든다 */
      const slab = (sx: number, sy: number, sz: number, px: number, py: number, pz: number, ry: number, rz: number) => {
        const g = bevelBox(sx, sy, sz, 0.01, 2);
        g.rotateZ(rz);
        g.rotateY(ry);
        g.translate(px, py, pz);
        paintGeometry(g, CLOTH);
        w.add(g, 'fabric', 0.8);
      };
      slab(d.w * 0.95, 0.045, d.d * 0.9, 0, 0.028, 0, 0.1, 0);

      w.part('속');
      // 접힌 비닐봉지 한 뭉치 — 닫혀 있으면 윗자락 밑에 묻혀 안 보인다
      const vinyl = bevelBox(0.13, 0.008, 0.095, 0.003, 1);
      vinyl.rotateY(-0.3);
      vinyl.translate(-0.02, 0.0505 + 0.004, -0.01);
      paintGeometry(vinyl, '#e3e6e4');
      w.add(vinyl, 'plastic', 0.2);

      w.movable('윗자락', () => {
        slab(d.w * 0.7, 0.05, d.d * 0.62, -0.03, 0.07, 0.02, -0.22, 0.06);
        slab(d.w * 0.4, 0.04, d.d * 0.4, 0.05, 0.1, -0.03, 0.35, -0.05);

        w.part('손잡이');
        // 끈 두 가닥이 꺼진 몸 위에 늘어진다 — 세운 손잡이는 서 있는 가방의 것
        for (const s of [-1, 1]) {
          const strap = bevelBox(0.022, 0.008, 0.24, 0.003);
          strap.rotateY(0.5 * s);
          strap.translate(s * 0.08, 0.105, 0.02 * s);
          paintGeometry(strap, '#c2bba6');
          w.add(strap, 'fabric', 0.7);
        }
      });
    },
  },
];
